import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, ExternalLink, Loader2 } from "lucide-react";
import { formatCurrency, monthNames, statusLabels, statusColors } from "@/lib/api";
import { useNavigate } from "react-router-dom";

/**
 * Affiche la liste détaillée des commandes d'une période sélectionnée
 * (drill-down) sous le graphique CA ou Commandes.
 *
 * Props :
 *  - mode : "orders" | "ca"
 *  - year / month : période cliquée
 *  - data : réponse du backend ({ orders, count, total_amount_ht | total_ca })
 *  - loading
 *  - onClose : reset la sélection
 */
export const DrillDownOrdersCard = ({ mode, year, month, data, loading, onClose }) => {
  const navigate = useNavigate();
  const isCa = mode === "ca";
  const total = isCa ? data?.total_ca : data?.total_amount_ht;
  const rows = data?.orders || [];
  const monthLabel = `${monthNames[month - 1]} ${year}`;

  return (
    <Card data-testid={`drilldown-${mode}-card`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="font-heading text-base">
          {isCa ? "CA" : "Commandes"} — {monthLabel}
          {typeof data?.count === "number" && (
            <span className="text-muted-foreground font-normal ml-2 text-sm">
              · {data.count} commande{data.count > 1 ? "s" : ""} · Total {formatCurrency(total || 0)}
            </span>
          )}
        </CardTitle>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          data-testid={`drilldown-close-${mode}`}
          title="Fermer"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-sm">
            Aucune commande pour cette période.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Référence</th>
                  <th>Client</th>
                  <th>Vendeur</th>
                  <th className="text-right">Montant HT</th>
                  {isCa && <th className="text-right">Avancement</th>}
                  {isCa && <th className="text-right">CA du mois</th>}
                  <th>Statut</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={`${o.id}-${o.source || ""}`} data-testid={`drilldown-row-${o.id}`}>
                    <td className="font-mono text-xs">{o.reference}</td>
                    <td className="font-medium">{o.client_name}</td>
                    <td className="text-sm text-muted-foreground">{o.seller_name || "-"}</td>
                    <td className="text-right currency tabular-nums">{formatCurrency(o.amount_ht || 0)}</td>
                    {isCa && (
                      <td className="text-right text-xs">
                        <div className="flex flex-col items-end gap-1">
                          <span className={`font-semibold tabular-nums ${(o.cumul_progress_percent || 0) > 100 ? "text-orange-600" : "text-teal-700"}`}>
                            {(o.cumul_progress_percent || 0).toFixed(0)}%
                          </span>
                          <div className="h-1 w-16 rounded-full bg-muted overflow-hidden">
                            <div
                              className={`h-full ${(o.cumul_progress_percent || 0) >= 100 ? "bg-green-500" : "bg-teal-500"}`}
                              style={{ width: `${Math.min(100, o.cumul_progress_percent || 0)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                    )}
                    {isCa && (
                      <td className="text-right currency font-medium text-primary tabular-nums">
                        {formatCurrency(o.month_ca || 0)}
                      </td>
                    )}
                    <td>
                      <Badge variant="outline" className={statusColors[o.status] || ""}>
                        {statusLabels[o.status] || o.status}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/commandes?focus=${o.id}`)}
                        title="Ouvrir la commande"
                        data-testid={`drilldown-open-${o.id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DrillDownOrdersCard;
